<template>
  <div
    id="product-form"
    ref="productForm"
    class="product-form"
    :class="{ [$class('loading')]: loading }"
    data-sign-post-location="main"
    js-sign-post
  >
    <form class="product-form__form" @submit.prevent="handleAddToCart">
      <product-options
        class="product-form__options"
        :loading="loading"
        :product="product"
        :section="section"
        :variant="variant"
        :bundle="bundle"
        @update-variant="handleUpdateVariant"
        @bundle-extra-item="bundleExtraItems"
        @update-bundle-status="handleBundleStatus"
      />

      <input name="quantity" readonly type="hidden" :value="quantity" />

      <recipient-form
        v-if="product.giftCard"
        class="product-form__recipient-form"
        :gift-card="giftCard"
        :section="section"
        @update-recipient-form="handleUpdateRecipientForm"
      />

      <low-inventory-alert :variant="variant" />

      <global-graduated-discounts
        v-if="variant"
        :variant="variant"
        :product="product"
        @update-quantity="handleUpdateQuantity"
        @update-discount="handleUpdateDiscount"
      />

      <product-prices
        class="product-form__prices-container"
        :compare-at-price="totalCompareAtPrice"
        :discount-logic="graduatedDiscountLogic"
        :loading="loading"
        :price="totalDiscountedPrice"
        template="form"
        :unit-price="variant?.unitPrice?.amount"
        :unit-price-measurement="variant?.unitPriceMeasurement"
        :varies="variant.price?.varies"
        :is-gift-card="product.giftCard"
      />

      <product-promoted-campaign
        v-if="getComponentVisible('product-promoted-campaign')"
        v-cloak
        class="product-form__promoted-campaign"
        :discount-logic="discountLogic"
        :state="state"
        :variant="variant"
        @add-to-cart="handleAddToCart"
      />

      <div
        v-if="
          !getComponentActive('product-promoted-campaign') &&
          loadProductPromotedCampaign
        "
        class="product-form__promoted-campaign"
        :class="[$class('loading')]"
      />

      <notify-me
        v-if="
          showBackInStockEmailForm &&
          (!isVariantAvailable ||
            isVariantBackSoonOrSoldOut ||
            bundleIsSoldout) &&
          product.type !== 'bundle'
        "
        ref="notifyMe"
        :variant-id="variant.id"
        :product-id="product.id"
        @update-success="handleNotifyMeSuccess"
      />

      <div
        v-if="showCta"
        ref="addToCart"
        class="product-form__add-to-cart-container"
        :class="{ [$class('fixed')]: isSticky }"
      >
        <btn
          class="product-form__add-to-cart"
          :class="{
            disabled: !isVariantAvailable && !showBackInStockEmailForm,
          }"
          :label="addToCartText"
          :modifiers="`center${!variant.available || bundleIsSoldout ? ' secondary' : ' cta'}`"
          :show-state-icon="state === 'adding'"
          show-icon
          :disabled="
            (!isVariantAvailable && !showBackInStockEmailForm) ||
            bundleIsSoldout
          "
          type="submit"
        >
          <template #icon>
            <component :is="addToCartIcon" />
          </template>

          <template #stateIcon>
            <icon-loading />
          </template>
        </btn>
      </div>

      <div
        v-if="isSticky"
        class="product-form__sticky-spacer"
        :style="{ height: `${spacerHeight}px` }"
      />
    </form>

    <notification-panel
      v-if="error"
      class-container="product-form__error"
      :show-close="false"
      :text="error"
      type="error"
    />

    <social-selling :product="product" />

    <delivery-time
      v-if="showDeliveryTime"
      class="product-form__delivery"
      :is-gift-card="product.giftCard"
    />
  </div>
</template>

<script>
/**
 * Vue: Product form (product-form)
 * -----------------------------------------------------------------------------
 * Product form to handle variant selection and adding to cart.
 *
 * @param {Boolean} [loading] - Loading state.
 * @param {Object|Boolean} product - Canvas formatted product object.
 * @param {Object} section - Main product section settings.
 * @param {Object|Boolean} variant - Canvas formatted variant object.
 * @param {Object} bundle - bundle meta object.
 *
 * @emits updateVariant - Emit new variant object.
 *
 */
// eslint-disable-next-line import/named
import { defineAsyncComponent } from 'vue';
import { mapActions, mapState } from 'pinia';
import useOverlaysStore from '~stores/overlays';
import useIndexStore from '~stores/index';
import useComponentsStore from '~stores/components';
import useCartStore from '~stores/cart';
import useDiscountCampaignsStore from '~stores/discount-campaigns';

import { formatTrackingItem } from '~/format/format-tracking-item';

import Btn from '~global/btn/btn';
import DeliveryTime from '~async/delivery-time/delivery-time';
import NotificationPanel from '~async/utils/notification-panel/notification-panel';
import NotifyMe from '~async/product-form/notify-me';
import ProductOptions from '~async/product-form/product-options';
import ProductPrices from '~global/product-prices/product-prices';
import LowInventoryAlert from '~async/low-inventory-alert/low-inventory-alert';
import RecipientForm from '~async/product-form/recipient-form';
import SocialSelling from '~async/social-selling/social-selling';
import GlobalGraduatedDiscounts from '~async/product-form/global-graduated-discounts';

import IconAdd from '~icons/general/add.svg';
import IconEmail from '~icons/general/email.svg';
import IconLoading from '~icons/general/loading.svg';

import { setIntersectionObserver } from '~/helpers/general';
import { computeDiscountLogic, emitUpdateVariant } from '~/helpers/vue';

export default {
  name: 'ProductForm',

  components: {
    Btn,
    DeliveryTime,
    GlobalGraduatedDiscounts,
    IconAdd,
    IconEmail,
    IconLoading,
    LowInventoryAlert,
    NotificationPanel,
    NotifyMe,
    ProductOptions,
    ProductPrices,
    ProductPromotedCampaign: defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.product-promoted-campaign' */
          '~async/product-promoted-campaign/product-promoted-campaign'
        ),
    }),
    RecipientForm,
    SocialSelling,
  },

  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    bundle: {
      type: Object,
      default: () => ({}),
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    section: {
      type: Object,
      default: () => ({
        id: '',
      }),
      required: true,
    },
    variant: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
  },

  emits: {
    'update-variant': emitUpdateVariant,
    'update-bundle-status': (payload) => {
      return typeof payload === 'boolean';
    },
  },

  data() {
    return {
      error: '',
      giftCard: {
        enableForm: false,
        fields: {
          Message: '',
          'Recipient email': '',
          'Recipient name': '',
          'Send on': '',
        },
        timezoneOffset: '',
      },
      isSticky: false,
      lineItemProperties: {},
      location: 'PDP',
      quantity: 1,
      showCta: true,
      spacerHeight: 0,
      state: 'ready',
      timeout: {},
      bundleExtraItem: {},
      bundleIsSoldout: false,
      selectedDiscount: null,
    };
  },

  computed: {
    ...mapState(useIndexStore, {
      isMobile: (store) => store.screen.mobile,
    }),

    ...mapState(useDiscountCampaignsStore, {
      getCampaignDiscount: 'getCampaignDiscount',
      promotedCampaign: 'promoted',
      activeCampaign: 'active',
      discountCode: 'code',
    }),
    ...mapState(useCartStore, {
      depositItemUpdate: 'depositItemUpdate',
    }),

    /**
     * Compute add to cart text.
     * @returns {String}
     */
    addToCartText() {
      if (!this.variant && !this.loading) {
        return this.$string('product.cta.select_variant');
      }

      if (this.variant && (!this.variant.available || this.bundleIsSoldout)) {
        if (this.showBackInStockEmailForm && this.product.type !== 'bundle') {
          return this.$string('product.cta.notify_me');
        }
        return this.$string('product.cta.sold_out');
      }

      return this.$string('product.cta.add_to_cart');
    },

    /**
     * Compute add to cart icon.
     * @returns {String}
     */
    addToCartIcon() {
      if (!this.variant || this.variant?.available) {
        return 'icon-add';
      }
      if (this.showBackInStockEmailForm && this.product.type !== 'bundle') {
        return 'icon-email';
      }
      return '';
    },

    /**
     * Compute discount logic.
     * @returns {Object}
     */
    discountLogic() {
      return computeDiscountLogic({
        discountPreview: !this.product.giftCard,
        onSale: this.variant?.compareAtPriceDiscount,
        product: this.product,
        variant: this.variant,
      });
    },

    /**
     * Compute discount logic for graduated discounts.
     * When a graduated discount is selected, disable campaign discounts
     * to prevent stacking discounts.
     * Also disable campaign discounts when fake compare-at price is set.
     * @returns {Object}
     */
    graduatedDiscountLogic() {
      if (
        this.selectedDiscountPercentage > 0 ||
        this.variant?.fakeCompareAtPrice
      ) {
        // Disable campaign discounts when graduated discount is active
        // or when fake compare-at price is set
        return {
          ...this.discountLogic,
          onSale: true, // Setting onSale to true prevents campaign discount from applying
        };
      }
      return this.discountLogic;
    },

    /**
     * Compute gift card line item properties.
     * @returns {Null|Object}
     */
    giftCardLineItemProperties() {
      if (
        !this.product.giftCard ||
        this.giftCard.fields['Recipient email'] === ''
      ) {
        return null;
      }

      const filteredFields = {};

      Object.entries(this.giftCard.fields).forEach(([key, value]) => {
        if (!value) {
          return;
        }

        const modifiedKey = `__${key.replace(/ /g, '_').toLowerCase()}`;

        filteredFields[modifiedKey] = value;
      });
      return {
        __shopify_send_gift_card_to_recipient: 'on',
        __shopify_offset: this.giftCard.timezoneOffset,
        ...filteredFields,
      };
    },

    /**
     * Compute whether to load product promoted campaign component.
     * checks also if the active campaign has graduated discounts
     * and show the component according to that
     * @returns {Boolean}
     */
    loadProductPromotedCampaign() {
      const campaign = this.promotedCampaign || this.isCurrentItemGraduated();
      return !this.loading && campaign;
    },

    /**
     * Checks if the current variant is available.
     * @returns {boolean} True if the variant exists and is available.
     */
    isVariantAvailable() {
      return this.variant?.available;
    },

    /**
     * Checks if the current variant's state is either 'back-soon'
     * or 'sold-out'.
     * @returns {boolean} True if the variant's availableState is 'back-soon'
     * or 'sold-out'.
     */
    isVariantBackSoonOrSoldOut() {
      return ['back-soon', 'sold-out'].includes(this.variant.availableState);
    },

    /**
     * Determines if the delivery time should be shown.
     * Combines the conditions from `showCta`, `isVariantAvailable`,
     * and `isVariantBackSoonOrSoldOut`. Shows the delivery time only, if
     * the product is available and the CTA is shown.
     * @returns {boolean} True if the delivery time should be shown.
     */
    showDeliveryTime() {
      return (
        this.showCta &&
        this.isVariantAvailable &&
        !this.isVariantBackSoonOrSoldOut
      );
    },

    showBackInStockEmailForm() {
      return this.variant.showBackInStockEmailForm;
    },

    /**
     * Total original price based on selected quantity.
     * @returns {Number}
     */
    totalOriginalPrice() {
      const unit = this.variant?.price?.amount || 0;
      return unit * this.quantity;
    },

    /**
     * Selected discount percentage from global graduated discounts.
     * @returns {Number}
     */
    selectedDiscountPercentage() {
      return this.selectedDiscount?.discountPercentage || 0;
    },

    /**
     * Total discounted price if a pack discount is selected; otherwise original total.
     * If fake compare-at price is set, display that as the price instead.
     * @returns {Number}
     */
    totalDiscountedPrice() {
      // If fake compare-at price is set, display that as the price
      if (this.variant?.fakeCompareAtPrice) {
        const fakePrice = parseFloat(this.variant.fakeCompareAtPrice);
        const calculatedPrice = fakePrice * 100 * this.quantity;
        // Convert to cents (multiply by 100) since prices are in cents format
        return calculatedPrice;
      }

      if (this.selectedDiscountPercentage > 0) {
        const discounted =
          this.totalOriginalPrice * (1 - this.selectedDiscountPercentage / 100);
        return Math.round(discounted);
      }
      return this.totalOriginalPrice;
    },

    /**
     * Compare-at total to show savings only when a pack discount applies.
     * Falls back to variant compareAtPrice when available and no pack discount.
     * If fake compare-at price is set, show the real price as the compare-at price.
     * @returns {Number|Boolean}
     */
    totalCompareAtPrice() {
      // If fake compare-at price is set, show the real price as the compare-at price
      if (this.variant?.fakeCompareAtPrice) {
        return this.totalOriginalPrice;
      }

      if (this.selectedDiscountPercentage > 0) {
        return this.totalOriginalPrice;
      }
      const compareUnit = this.variant?.compareAtPrice?.amount;
      return compareUnit ? compareUnit * this.quantity : false;
    },
  },

  watch: {
    /**
     * Watch isMobile to clear isSticky.
     */
    isMobile() {
      if (this.isMobile) {
        return;
      }

      this.isSticky = false;
    },

    /**
     * Watch load state to load component.
     */
    loadProductPromotedCampaign() {
      if (!this.loadProductPromotedCampaign) {
        return;
      }

      this.loadComponent('product-promoted-campaign');
    },

    /**
     * Watch product to reset quantity and error.
     */
    product() {
      this.quantity = 1;
      this.error = '';
    },

    /**
     * Watch variant to reset error and show CTA states.
     */
    variant() {
      this.error = '';
      this.showCta = true;
      this.selectedDiscount = null;
    },

    /**
     * Watch quantity to update price display
     */
    quantity() {
      // Force update to refresh price display
      this.$nextTick().then(() => {
        this.$forceUpdate();
      });
    },
  },

  beforeMount() {
    this.giftCard.timezoneOffset = new Date().getTimezoneOffset().toString();
  },

  mounted() {
    this.setStickyCta();
  },

  methods: {
    ...mapActions(useComponentsStore, {
      loadComponent: 'load',
    }),
    ...mapActions(useCartStore, {
      addItem: 'addItem',
      setDepositItemUpdate: 'setDepositItemUpdate',
    }),
    ...mapActions(useOverlaysStore, {
      openOverlay: 'open',
    }),

    /**
     * Push Custom Product Properties to Line Item Properties from Bundle
     * Item from the bundle where customers cannot select anything
     */
    bundleExtraItems(offsetBundle) {
      // const properties = {}
      if (offsetBundle) {
        const mappedBundles = offsetBundle.map((item) => {
          return item.currentOptionName;
        });

        this.lineItemProperties.extraBundle = mappedBundles;
      }
    },

    /**
     * Set intersection observer for sticky CTA.
     */
    setStickyCta() {
      if (!this.$refs.productForm || this.$variable('storybook')) {
        return;
      }

      setIntersectionObserver({
        callback: this.handleIntersection,
        element: this.$refs.productForm,
        threshold: [0, 0.25, 0.5, 0.75, 1],
      });
    },

    /**
     * Handle intersection.
     * @param {Array} entries - Intersection entries.
     */
    handleIntersection(entries) {
      if (!this.isMobile) {
        return;
      }

      entries.forEach((entry) => {
        this.isSticky =
          !entry.isIntersecting && entry.boundingClientRect.bottom < 0;

        if (!this.isSticky) {
          cnvs.EventBus.emit('updateSticky', this.isSticky);
          return;
        }

        this.spacerHeight = this.$refs.addToCart.offsetHeight;

        // Emit an event when isSticky changes
        cnvs.EventBus.emit('updateSticky', this.isSticky);
      });
    },

    /**
     * Handle add to cart submission.
     */
    async handleAddToCart() {
      let depositVariantId;

      if (!this.variant) {
        this.error = this.$string('product.form.select_variant');
        return;
      }

      clearTimeout(this.timeout);
      this.error = '';
      this.state = 'adding';

      /**
       * Notify me.
       * - Uses child component method.
       */
      if (!this.variant.available) {
        try {
          await this.$refs.notifyMe.handleNotifyMe();
          this.state = 'ready';
        } catch (error) {
          this.state = 'error';
          cnvs.ReportError('Failed to submit notify me', error);
        }

        return;
      }

      /**
       * Storybook.
       */
      if (this.$variable('storybook')) {
        if (!this.variant.available) {
          this.error = this.$string('product.form.sold_out');
          this.state = 'ready';
          return;
        }

        this.timeout = setTimeout(() => {
          this.state = 'ready';
        }, this.$timing('message'));

        return;
      }

      const items = [
        {
          id: this.variant.id,
          properties: this.product.giftCard
            ? {
                ...this.giftCardLineItemProperties,
                ...this.lineItemProperties,
              }
            : this.lineItemProperties,
          bundle: this.product.type === 'bundle',
          quantity: this.quantity,
        },
      ];

      if (
        this.variant.canDeposit === 'true' &&
        this.$variable('store.country') === 'DE'
      ) {
        depositVariantId = this.variant.id;
        this.setDepositItemUpdate([
          ...this.depositItemUpdate,
          depositVariantId,
        ]);

        const depositProduct = {
          id: this.$variable('product.depositProductID'),
          quantity: this.variant.canDepositAmount,
        };
        items.push(depositProduct);
      }

      /**
       * Add to cart.
       */
      try {
        await this.addItem(items);
        this.state = 'added';

        // Tracking event add qty to item in cart
        this.handleTrackingAddItem();

        this.openOverlay({
          component: 'cart-drawer',
          ignoreDismissed: true,
          namespace: 'cartDrawer',
        });

        await this.resetState();
      } catch (error) {
        this.state = 'error';

        if (error.description) {
          this.handleErrorMessage(error.description);
          return;
        }

        cnvs.ReportError('Failed to add product to cart', error);
      }

      this.setDepositItemUpdate(
        this.depositItemUpdate.filter((id) => id !== depositVariantId),
      );
    },

    /**
     * Reset product form state after adding to cart after message delay.
     */
    async resetState() {
      await new Promise((resolve) => {
        setTimeout(resolve, this.$timing('message'));
      });

      this.state = 'ready';

      /**
       * Reset gift card recipient form based on setting.
       */
      if (this.product.giftCard) {
        this.giftCard = {
          enableForm: false,
          fields: {
            Message: '',
            'Recipient email': '',
            'Recipient name': '',
            'Send on': '',
          },
        };
      }
    },

    /**
     * Handle update variant event.
     * @param {Object} variant - Emitted variant object.
     */
    handleUpdateVariant(variant) {
      this.$emit('update-variant', variant);
    },

    /**
     * Handles the add to cart button depending on bundle status
     * @param status Boolean
     */
    handleBundleStatus(status) {
      this.bundleIsSoldout = status;
      this.$emit('update-bundle-status', status);
    },

    /**
     * Handle update quantity event.
     * @param {String} quantity - Emitted quantity.
     */
    handleUpdateQuantity(quantity) {
      this.quantity = quantity;

      // Force update to refresh price display
      this.$nextTick().then(() => {
        this.$forceUpdate();
      });
    },

    /**
     * Handle update discount event.
     * @param {Object} discount - Emitted discount object.
     */
    handleUpdateDiscount(discount) {
      this.selectedDiscount = discount;
    },

    /**
     * Handle update on the notify me component - if requests has success.
     * @param {Boolean} success - Wether the request was successful or not.
     */
    handleNotifyMeSuccess(success) {
      if (success) {
        this.showCta = false;
      } else {
        this.state = 'error';
      }
    },

    /**
     * Handle update recipient form event.
     * @param {Object} recipientForm - Emitted recipient form.
     */
    handleUpdateRecipientForm(recipientForm) {
      this.giftCard = recipientForm;
    },

    /**
     * Handle error message.
     * @param {Object} errorDescription - Error description from add request.
     */
    handleErrorMessage(errorDescription) {
      if (typeof errorDescription === 'string') {
        this.error = errorDescription;
        return;
      }

      this.error = Object.values(errorDescription)
        .flat()
        .map((error) => {
          return error.endsWith('.') ? error : `${error}.`;
        })
        .join('<br>');
    },

    /**
     * Tracking event add qty to item in cart.
     */
    handleTrackingAddItem() {
      const percentageDiscount = this.getCampaignDiscount(
        this.discountLogic,
        'active',
      );
      const discountObject = {
        percentageDiscount,
        discountCode: this.discountCode,
      };
      const deliveryTime = this.$string('product.delivery_time', {
        replace: { time: this.$variable('product.deliveryTime') },
      });
      const currencyCode = this.$variable('store.currency');

      const item = formatTrackingItem({
        product: this.product,
        variant: this.variant,
        template: 'PDP',
        discountObject,
        deliveryTime,
        currencyCode,
        quantity: this.quantity,
      });

      cnvs.EventBus.emit('dataLayer:add_to_cart', {
        event: 'add_to_cart',
        currency: cnvs.store.currency,
        location: 'PDP',
        items: [item],
      });
    },

    /**
     * Check if variant is part of the graduated discount
     * we have to wait here as via search the variantId isn't available right away
     * @returns {Boolean}
     */
    async isCurrentItemGraduated() {
      await this.$nextTick();
      const variantId = this.getVariantFromUrl();
      if (!this.activeCampaign?.graduatedDiscounts || !variantId) {
        return false;
      }
      const tiers = this.activeCampaign?.graduatedDiscounts || [];
      for (const tier of tiers) {
        if (tier.variantIds.includes(variantId.toString())) {
          return true;
        }
      }

      return false;
    },

    /**
     * Get initial variant from URL parameter.
     * @returns {String}
     */
    getVariantFromUrl() {
      const params = new URLSearchParams(document.location.search.substring(1));
      const variantId = Number(params.get('variant'));
      return variantId;
    },
  },
};
</script>

<style lang="scss">
@import './product-form';
</style>
